import React, { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import { Toaster, toast } from "react-hot-toast";
import { Dialog, Transition, Listbox } from "@headlessui/react";
import {
  Building2, RefreshCcw, Search, CreditCard, Link2, Truck, RotateCcw, HelpCircle,
  ChevronsUpDown, Check, CheckCircle2, XCircle, AlertTriangle, CircleDot, CircleDashed,
  Wallet, IndianRupee, Clock4, PackageCheck
} from "lucide-react";
import useOrders from "../Services/OrderServices/useOrders";
import useOrdersMain from "../Services/OrdersMainServices/useOrdersMain";

/* ---------- Utils ---------- */
const cls = (...s) => s.filter(Boolean).join(" ");
const fmt = (n) => (n === undefined || n === null || n === "" || isNaN(Number(n)) ? "0.00" : Number(n).toFixed(2));
const safeParse = (str) => { try { return JSON.parse(str || "{}"); } catch { return {}; } };
const parseCustomization = (custStr) => {
  const obj = typeof custStr === "string" ? safeParse(custStr) : (custStr || {});
  return Object.entries(obj);
};

const STATUSES = Array.from(new Set([
  "received", "paid", "accepted", "prepared", "rejected", "refunded",
  "driver_assigned", "driver_confirmed", "driver_pickup_arrival", "delivery_picked", "driver_dropoff_arrival",
  "completed", "dispatched", "delivered", "ready_for_dinning", "ready_for_takeway", "not-delivered"
])).sort();

const PER_PAGE = 35;
const badgeTone = (kind, valueRaw) => {
  const v = String(valueRaw || "").toLowerCase();
  if (kind === "order") {
    if (["completed", "delivered", "accepted", "prepared", "paid"].includes(v)) return { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", Icon: CheckCircle2 };
    if (["rejected", "refunded", "not-delivered"].includes(v)) return { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", Icon: XCircle };
    if (["dispatched", "driver_assigned", "driver_confirmed", "driver_pickup_arrival", "delivery_picked", "driver_dropoff_arrival", "ready_for_dinning", "ready_for_takeway", "received"].includes(v)) return { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-700", Icon: CircleDot };
    return { bg: "bg-gray-50", border: "border-gray-200", text: "text-gray-700", Icon: CircleDashed };
  }
  if (kind === "payment") {
    if (["paid", "success", "captured"].includes(v)) return { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", Icon: Wallet };
    if (["unpaid", "failed"].includes(v)) return { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", Icon: XCircle };
    return { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", Icon: AlertTriangle };
  }
  if (kind === "delivery") {
    if (["delivered"].includes(v)) return { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", Icon: PackageCheck };
    if (["dispatched", "driver_assigned", "driver_confirmed", "driver_pickup_arrival", "delivery_picked", "driver_dropoff_arrival"].includes(v)) return { bg: "bg-indigo-50", border: "border-indigo-200", text: "text-indigo-700", Icon: Truck };
    if (["not-delivered"].includes(v)) return { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", Icon: XCircle };
    return { bg: "bg-gray-50", border: "border-gray-200", text: "text-gray-700", Icon: CircleDashed };
  }
  return { bg: "bg-gray-50", border: "border-gray-200", text: "text-gray-700", Icon: CircleDashed };
};
function StatusBadge({ kind, value, extra }) {
  const { bg, border, text, Icon } = badgeTone(kind, value);
  return (
    <span className={cls("inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold", bg, border, text)}>
      <Icon size={13} />
      <span className="capitalize">{value || "—"}</span>
      {extra ? <span className="opacity-70">({extra})</span> : null}
    </span>
  );
}
function ConfirmDialog({
  open,
  onClose,
  title = "Confirm",
  body,
  confirmText = "Confirm",
  tone = "primary",
  busy = false,
  confirmDisabled = false, // ✅ NEW
  onConfirm,
}) {
  const btnTone =
    tone === "danger"
      ? "bg-rose-600 hover:bg-rose-700"
      : tone === "success"
        ? "bg-emerald-600 hover:bg-emerald-700"
        : "bg-sky-600 hover:bg-sky-700";

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={() => !busy && onClose?.()} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 grid place-items-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
              <Dialog.Title className="text-base font-semibold">
                {title}
              </Dialog.Title>

              <div className="mt-2 text-sm text-gray-700">{body}</div>

              <div className="mt-4 flex justify-end gap-2">
                <button
                  className="h-9 rounded-xl border px-3 text-sm"
                  onClick={onClose}
                  disabled={busy}
                >
                  Cancel
                </button>

                <button
                  className={cls(
                    "h-9 rounded-xl px-4 text-sm font-medium text-white",
                    btnTone,
                    (busy || confirmDisabled) && "opacity-60 cursor-not-allowed"
                  )}
                  onClick={onConfirm}
                  disabled={busy || confirmDisabled} // ✅ NEW
                >
                  {busy ? "Working…" : confirmText}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
function SelectDialog({ open, onClose, title = "Select", options = [], selected, setSelected, confirmText = "Apply", busy = false, onConfirm }) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={() => !busy && onClose?.()} className="relative z-50">
        <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>
        <div className="fixed inset-0 grid place-items-center p-4">
          <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-100" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
            <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
              <Dialog.Title className="text-base font-semibold mb-2">{title}</Dialog.Title>
              <Listbox value={selected} onChange={setSelected}>
                <div className="relative">
                  <Listbox.Button className="relative w-full cursor-default rounded-xl border bg-white py-2 pl-3 pr-10 text-left text-sm">
                    <span className="block truncate">{selected?.label || "Select"}</span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronsUpDown className="h-4 w-4 opacity-60" />
                    </span>
                  </Listbox.Button>
                  <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-xl border bg-white py-1 text-sm shadow-lg">
                      {options.map((opt) => (
                        <Listbox.Option key={opt.value} value={opt} className={({ active }) => cls("relative cursor-default select-none px-3 py-2", active && "bg-gray-50")}>
                          {({ selected }) => (
                            <div className="flex items-center gap-2">
                              <span className={cls("grid h-5 w-5 place-items-center rounded border", selected ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "text-transparent")}>
                                <Check size={14} />
                              </span>
                              <span className={cls("block truncate", selected && "font-medium")}>{opt.label}</span>
                            </div>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
              <div className="mt-4 flex justify-end gap-2">
                <button className="h-9 rounded-xl border px-3 text-sm" onClick={onClose} disabled={busy}>Cancel</button>
                <button className="h-9 rounded-xl bg-amber-600 px-4 text-sm font-medium text-white hover:bg-amber-700" onClick={onConfirm} disabled={busy}>
                  {busy ? "Working…" : confirmText}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
function PaytmStatusDialog({ open, onClose, loading = false, data }) {
  const r = data?.response || {};
  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={() => !loading && onClose?.()} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 grid place-items-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Dialog.Title className="text-base font-semibold">Payment Gateway Status</Dialog.Title>
                  <div className="text-xs text-gray-500">Paytm polled status for this order</div>
                </div>
                <button className="h-8 rounded-xl border px-3 text-sm" onClick={onClose} disabled={loading}>
                  Close
                </button>
              </div>

              <div className="mt-4">
                {loading ? (
                  <div className="py-8 text-center text-sm text-gray-500">Checking…</div>
                ) : !data ? (
                  <div className="py-8 text-center text-sm text-gray-500">No status yet.</div>
                ) : (
                  <div className="space-y-3 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge kind="payment" value={r.transactionStatus || "—"} />
                      <span className="text-xs text-gray-500">Order #{r.orderId || "—"}</span>
                    </div>

                    <div className="rounded-xl border bg-gray-50 p-3">
                      <div className="grid grid-cols-1 gap-2">
                        <div><b>Transaction ID:</b> {r.transactionId || "—"}</div>
                        <div><b>Bank Transaction ID:</b> {r.bankTransactionId || "—"}</div>
                        <div className="flex items-center gap-2"><Clock4 size={14} className="opacity-70" /> <b>Polled At:</b> {r.polledAt || "—"}</div>
                      </div>
                    </div>

                    {data?.responseMessage && (
                      <div className="text-xs text-gray-500">{data.responseMessage}</div>
                    )}
                  </div>
                )}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
function EasebuzzRefundStatusDialog({ open, onClose, loading = false, data }) {
  const r = data?.response || {};
  const refunds = Array.isArray(r?.refunds) ? r.refunds : [];
  const firstRefund = refunds[0] || {};

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={() => !loading && onClose?.()} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 grid place-items-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-2xl rounded-2xl bg-white p-5 shadow-xl">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Dialog.Title className="text-base font-semibold">
                    Easebuzz Refund Status
                  </Dialog.Title>
                  <div className="text-xs text-gray-500">
                    Refund transaction confirmation details
                  </div>
                </div>
                <button
                  className="h-8 rounded-xl border px-3 text-sm"
                  onClick={onClose}
                  disabled={loading}
                >
                  Close
                </button>
              </div>

              <div className="mt-4">
                {loading ? (
                  <div className="py-8 text-center text-sm text-gray-500">
                    Checking…
                  </div>
                ) : !data ? (
                  <div className="py-8 text-center text-sm text-gray-500">
                    No status found.
                  </div>
                ) : (
                  <div className="space-y-4 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge
                        kind="payment"
                        value={firstRefund?.refund_status || (r?.status ? "success" : "failed")}
                      />
                      <span className="text-xs text-gray-500">
                        {data?.responseMessage || "—"}
                      </span>
                    </div>

                    <div className="rounded-xl border bg-gray-50 p-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <div><b>Txn ID:</b> {r?.txnid || "—"}</div>
                      <div><b>Easebuzz ID:</b> {r?.easebuzz_id || "—"}</div>
                      <div><b>Amount:</b> ₹{r?.amount || "—"}</div>
                      <div><b>Net Debit:</b> ₹{r?.net_amount_debit || "—"}</div>
                    </div>

                    <div className="overflow-auto rounded-xl border">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr className="text-left">
                            {[
                              "Refund ID",
                              "Status",
                              "Merchant Refund ID",
                              "Refund Amount",
                              "Refund Date",
                              "Settled Date",
                              "ARN",
                              "Failure Reason",
                            ].map((h) => (
                              <th key={h} className="px-3 py-2 font-semibold">
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {refunds.length === 0 ? (
                            <tr>
                              <td colSpan={8} className="px-3 py-4 text-center text-gray-500">
                                No refund rows found.
                              </td>
                            </tr>
                          ) : (
                            refunds.map((item, idx) => (
                              <tr key={`${item.refund_id}-${idx}`} className="border-t">
                                <td className="px-3 py-2">{item.refund_id || "—"}</td>
                                <td className="px-3 py-2">
                                  <StatusBadge kind="payment" value={item.refund_status || "—"} />
                                </td>
                                <td className="px-3 py-2">{item.merchant_refund_id || "—"}</td>
                                <td className="px-3 py-2">₹{item.refund_amount || "—"}</td>
                                <td className="px-3 py-2">{item.merchant_refund_date || "—"}</td>
                                <td className="px-3 py-2">{item.refund_settled_date || "—"}</td>
                                <td className="px-3 py-2">{item.arn_number || "—"}</td>
                                <td className="px-3 py-2">{item.failure_description || "—"}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
/* ---------- Main ---------- */
export default function ReconOrderController() {
  const { api, adminId, AllClients } = useContext(GlobalContext);

  const { getAllCatalogueOrders, getOrderDetails, updateOrderPaymentStatus, getPaytmTransactionStatus, requestCustomerAddress, assignDeliveryPartner, getRefundReport, processRefund, easebuzzRefund, getAllEaseBuzzRefund,
    checkStatusEasebuzzRefund, generatePaymentIntent, } = useOrders();
  const { changeCatalogOrderStatus } = useOrdersMain();
  const runInBackground = (task) => {
    Promise.resolve()
      .then(task)
      .catch((err) => {
        console.error("Background refresh failed:", err);
      });
  };
  // geo
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);

  // clients
  const [clients, setClients] = useState([]);

  // table
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [clientFilter, setClientFilter] = useState("");

  // tabs
  const [tab, setTab] = useState("search"); // table | search | refunds | settings

  // pagination
  const filteredOrders = useMemo(() => {
    if (!searchTerm.trim()) return orders;
    const q = searchTerm.toLowerCase();
    return orders.filter((o) =>
      Object.values(o).some((v) =>
        String(v ?? "")
          .toLowerCase()
          .includes(q),
      ),
    );
  }, [orders, searchTerm]);
  const [page, setPage] = useState(1);
  const PER_PAGE = 35;
  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PER_PAGE));
  const pageRows = useMemo(
    () => filteredOrders.slice((page - 1) * PER_PAGE, page * PER_PAGE),
    [filteredOrders, page],
  );

  // details modal
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsBusy, setDetailsBusy] = useState(false);
  const [detailsRow, setDetailsRow] = useState(null);
  const [details, setDetails] = useState({});
  const [rowStatus, setRowStatus] = useState("");

  // confirms/select
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmCfg, setConfirmCfg] = useState({
    title: "",
    body: "",
    tone: "primary",
    onConfirm: null,
    busy: false,
    confirmDisabled: false,
  });
  const [selectOpen, setSelectOpen] = useState(false);
  const [selectCfg, setSelectCfg] = useState({
    title: "",
    options: [],
    selected: null,
    setSelected: () => { },
    onConfirm: null,
    busy: false,
  });

  // paytm status
  const [paytmOpen, setPaytmOpen] = useState(false);
  const [paytmLoading, setPaytmLoading] = useState(false);
  const [paytmData, setPaytmData] = useState(null);

  // search tab
  const [searchId, setSearchId] = useState("");
  const [searchObj, setSearchObj] = useState({});
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchStatus, setSearchStatus] = useState("");
  const [searchUpdating, setSearchUpdating] = useState(false);

  // refunds tab
  const [refunds, setRefunds] = useState([]);
  const [refundLoading, setRefundLoading] = useState(false);
  // easebuzz refunds
  const [refundTab, setRefundTab] = useState("current"); // current | easebuzz
  const [easebuzzRefunds, setEasebuzzRefunds] = useState([]);
  const [easebuzzRefundLoading, setEasebuzzRefundLoading] = useState(false);
  const [easebuzzRefundPage, setEasebuzzRefundPage] = useState(1);
  const [easebuzzRefundLimit] = useState(20);
  const [easebuzzRefundTotalOnPage, setEasebuzzRefundTotalOnPage] = useState(0);

  // easebuzz refund status modal
  const [easebuzzStatusOpen, setEasebuzzStatusOpen] = useState(false);
  const [easebuzzStatusLoading, setEasebuzzStatusLoading] = useState(false);
  const [easebuzzStatusData, setEasebuzzStatusData] = useState(null);
  // dates
  const getOneDayBack = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const getToday = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const startDate = getOneDayBack();
  const endDate = getToday();

  /* ---------- API calls ---------- */
  useEffect(() => {
    setClients(AllClients);
  }, [AllClients]);

  const fetchOrders = async () => {
    try {
      setLoading(true);

      const result = await getAllCatalogueOrders({
        adminId,
        userid: clientFilter,
        statuses: [
          "received",
          "paid",
          "accepted",
          "prepared",
          "rejected",
          "refunded",
          "driver_assigned",
          "driver_confirmed",
          "driver_pickup_arrival",
          "delivery_picked",
          "driver_dropoff_arrival",
        ],
        fromdate: startDate,
        todate: endDate,
        latitude,
        longitude,
      });

      if (result.success) {
        setOrders(result.data);
      } else {
        setOrders([]);
        toast.error(result.message);
      }
    } finally {
      setLoading(false);
    }
  };
  const fetchOrderDetails = async (orderid, setState = true) => {
    try {
      setDetailsBusy(true);

      const data = await getOrderDetails(orderid);

      if (setState) {
        setDetails(data && Object.keys(data).length > 0 ? data : {});
      }

      return data || {};
    } catch (e) {
      console.error(e);
      if (setState) setDetails({});
      return {};
    } finally {
      setDetailsBusy(false);
    }
  };

  /* ---------- Confirm helpers ---------- */
  const confirmAction = ({
    title,
    body,
    tone = "primary",
    confirmDisabled = false,
    onConfirm,
  }) => {
    setConfirmCfg({
      title,
      body,
      tone,
      busy: false,
      confirmDisabled,
      onConfirm: async () => {
        try {
          setConfirmCfg((c) => ({ ...c, busy: true }));
          await onConfirm?.(); // only main action should be awaited
          setConfirmOpen(false); // close immediately after action finishes
        } finally {
          setConfirmCfg((c) => ({ ...c, busy: false }));
        }
      },
    });
    setConfirmOpen(true);
  };
  const updatePaymentStatus = (orderid) => {
    confirmAction({
      title: "Update Payment Status?",
      body: "You are about to update the payment status for this order.",
      onConfirm: async () => {
        const result = await updateOrderPaymentStatus(orderid);

        if (result.success) {
          const d = result.data;

          toast.success(
            `Payment updated: ${d?.status || ""} • Ref: ${d?.refid || ""}`
          );

          runInBackground(() => fetchOrderDetails(orderid));
          runInBackground(() => fetchOrders());
        } else {
          toast.error(result.message);
        }
      },
    });
  };

  const checkPaytmStatus = async (orderid) => {
    if (!orderid) {
      toast.error("Missing order id");
      return;
    }

    setPaytmOpen(true);
    setPaytmLoading(true);
    setPaytmData(null);

    try {
      const result = await getPaytmTransactionStatus(orderid);

      if (result.success) {
        setPaytmData(result.data);
        toast.success(
          `Status: ${result.data?.response?.transactionStatus || "—"
          }`
        );
      } else {
        setPaytmData(result.data);
        toast.error(result.message);
      }
    } finally {
      setPaytmLoading(false);
    }
  };

  const requestAddress = (orderid) => {
    confirmAction({
      title: "Request Customer Address?",
      body: "Send a WhatsApp address request for this order.",
      onConfirm: async () => {
        const result = await requestCustomerAddress(orderid);

        if (result.success) {
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }
      },
    });
  };

  const openSendToDeliveryPartner = (orderid, clientUserId) => {
    const options = [
      { value: "", label: "None (Prepaid/Null)" },
      { value: "COD", label: "Cash on Delivery" },
    ];

    const [first] = options;

    setSelectCfg({
      title: "Send to Delivery Partner",
      options,
      selected: first,
      setSelected: (v) => setSelectCfg((c) => ({ ...c, selected: v })),
      busy: false,
      onConfirm: async () => {
        setSelectCfg((c) => ({ ...c, busy: true }));

        try {
          const result = await assignDeliveryPartner({
            userid: clientUserId,
            orderId: orderid,
            platform: "whatsapp",
            paymentMode: selectCfg.selected?.value || null,
            deliveryPartner: "packky",
          });

          if (result.success) {
            toast.success("Delivery Partner assigned successfully");
            setSelectOpen(false);

            runInBackground(() => fetchOrderDetails(orderid));
            runInBackground(() => fetchOrders());
          } else {
            toast.error(result.message);
          }
        } finally {
          setSelectCfg((c) => ({ ...c, busy: false }));
        }
      },
    });

    setSelectOpen(true);
  };

  const changeOrderStatusDirect = async (
    ctx,
    chosenStatus,
    orderid,
    clientUserId
  ) => {
    if (!orderid) return toast.error("Order id missing");
    if (!chosenStatus) return toast.error("Select a status first");
    if (!clientUserId) return toast.error("Client id (userid) missing");

    setDetailsBusy(true);

    try {
      const result = await changeCatalogOrderStatus({
        userid: clientUserId,
        orderid,
        status: chosenStatus,
      });

      if (result.success) {
        toast.success(result.message);

        runInBackground(() => fetchOrders());
        runInBackground(() => fetchOrderDetails(orderid));
      } else {
        toast.error(result.message);
      }
    } finally {
      setDetailsBusy(false);
    }
  };
  const confirmChangeOrderStatus = (chosenStatus, orderid, clientUserId) => {
    confirmAction({
      title: "Change Order Status?",
      body: (
        <>
          Set status to <b className="capitalize">{chosenStatus || "(none)"}</b>{" "}
          for order <b>#{orderid}</b>?
        </>
      ),
      onConfirm: async () =>
        changeOrderStatusDirect("modal", chosenStatus, orderid, clientUserId),
    });
  };
  const clientUserId =
    details?.fk_user_id || details?.us_code || details?.userid || "";

  const fetchSearch = async (oid) => {
    if (!oid) return;

    setSearchLoading(true);

    try {
      const data = await getOrderDetails(oid);

      if (data && Object.keys(data).length > 0) {
        setSearchObj(data);
        setSearchStatus(data?.status || "");
      } else {
        setSearchObj({});
      }
    } catch (e) {
      console.error(e);
      setSearchObj({});
    } finally {
      setSearchLoading(false);
    }
  };

  const fetchRefunds = async () => {
    setRefundLoading(true);

    try {
      const result = await getRefundReport({ userid: "" });

      if (result.success) {
        setRefunds(result.data);
      } else {
        setRefunds([]);
        toast.error(result.message);
      }
    } finally {
      setRefundLoading(false);
    }
  };
  const fetchEasebuzzRefunds = async (targetPage = easebuzzRefundPage) => {
    setEasebuzzRefundLoading(true);

    try {
      const result = await getAllEaseBuzzRefund({
        page: targetPage,
        limit: easebuzzRefundLimit,
      });

      if (result.success) {
        const rows = Array.isArray(result.data) ? result.data : [];

        setEasebuzzRefunds(rows);
        setEasebuzzRefundTotalOnPage(rows.length);

        if (targetPage > 1 && rows.length === 0) {
          const prevPage = targetPage - 1;
          setEasebuzzRefundPage(prevPage);
          return;
        }
      } else {
        setEasebuzzRefunds([]);
        setEasebuzzRefundTotalOnPage(0);
        toast.error(result.message || "Failed to fetch Easebuzz refunds");
      }
    } catch (e) {
      console.error(e);
      setEasebuzzRefunds([]);
      setEasebuzzRefundTotalOnPage(0);
      toast.error("Failed to fetch Easebuzz refunds");
    } finally {
      setEasebuzzRefundLoading(false);
    }
  };
  const checkEasebuzzRefundStatus = async (row) => {
    if (!row?.easebuzzid) {
      toast.error("Missing Easebuzz ID");
      return;
    }

    setEasebuzzStatusOpen(true);
    setEasebuzzStatusLoading(true);
    setEasebuzzStatusData(null);

    try {
      const result = await checkStatusEasebuzzRefund({
        easebuzz_id: row.easebuzzid,
        merchant_refund_id: "",
        refund_amount: 0,
        key: "",
        hash: "",
      });

      if (result.success) {
        setEasebuzzStatusData(result.data);
      } else {
        setEasebuzzStatusData(result.data || null);
        toast.error(result.message || "Failed to fetch refund status");
      }
    } catch (e) {
      console.error(e);
      setEasebuzzStatusData(null);
      toast.error("Failed to fetch refund status");
    } finally {
      setEasebuzzStatusLoading(false);
    }
  };
  const refundPayment = (orderObj) => {

    if (!orderObj) return;

    const amount = Math.trunc(Number(orderObj?.pay_amount ?? 0)).toString();
    const transactionId = String(orderObj?.virtual_acc || "");
    if (!transactionId)
      return toast.error("Missing transaction id (virtual account).");

    if (!transactionId) {
      toast.error("Missing transaction id (virtual account).");
      return;
    }

    // local variable that will be updated from textarea
    let remarkValue = "";

    confirmAction({
      title: "Confirm Refund",
      tone: "danger",
      confirmDisabled: true, // start disabled
      body: (
        <div className="text-sm space-y-3">
          <div>
            <b>Transaction ID:</b> {transactionId}
          </div>
          <div>
            <b>Amount:</b> ₹{amount}
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">Remark *</label>
            <textarea
              className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-rose-200"
              rows={3}
              placeholder="Enter remark (required)"
              onChange={(e) => {
                const v = e.target.value || "";
                remarkValue = v;

                // enable/disable confirm button live
                setConfirmCfg((c) => ({
                  ...c,
                  confirmDisabled: v.trim().length === 0,
                }));
              }}
            />
            <div className="mt-1 text-xs text-gray-500">
              Remark is required to proceed.
            </div>
          </div>
        </div>
      ),
      onConfirm: async () => {
        const remark = (remarkValue || "").trim();
        if (!remark) {
          toast.error("Please enter a remark.");
          return;
        }

        const payload = {
          userid:
            orderObj?.fk_user_id || orderObj?.us_code || clientFilter || "",
          amount,
          transactionId,
          remark,
        };

        const result = await processRefund(payload);

        if (result.success) {
          toast.success(result.message);

          runInBackground(() => fetchOrderDetails(orderObj.orderid));
          runInBackground(() => fetchOrders());
          runInBackground(() => fetchRefunds());
        } else {
          toast.error(result.message);
        }
      },
    });
  };
  const refundEasebuzzPayment = (orderObj) => {
    if (!orderObj?.orderid) {
      toast.error("Missing order id.");
      return;
    }

    const maxAmount = Number(orderObj?.pay_amount ?? 0);

    if (!Number.isFinite(maxAmount) || maxAmount <= 0) {
      toast.error("Invalid order amount.");
      return;
    }

    let refundAmount = maxAmount.toFixed(2);
    let remarkValue = "";

    confirmAction({
      title: "Confirm Easebuzz Refund",
      tone: "danger",
      confirmDisabled: true,
      body: (
        <div className="text-sm space-y-3">
          <div>
            <b>Order ID:</b> {orderObj.orderid}
          </div>

          <div>
            <b>Max Refundable Amount:</b> ₹{maxAmount.toFixed(2)}
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">Refund Amount *</label>
            <input
              type="number"
              min="0"
              step="0.01"
              defaultValue={refundAmount}
              className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-200"
              placeholder="Enter refund amount"
              onChange={(e) => {
                refundAmount = e.target.value || "";

                const amt = Number(refundAmount);
                const remarkOk = (remarkValue || "").trim().length > 0;
                const amountOk =
                  refundAmount !== "" &&
                  Number.isFinite(amt) &&
                  amt > 0 &&
                  amt <= maxAmount;

                setConfirmCfg((c) => ({
                  ...c,
                  confirmDisabled: !(remarkOk && amountOk),
                }));
              }}
            />
            <div className="mt-1 text-xs text-gray-500">
              Refund amount must be greater than 0 and cannot exceed ₹{maxAmount.toFixed(2)}.
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">Remark *</label>
            <textarea
              className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-200"
              rows={3}
              placeholder="Enter remark (required)"
              onChange={(e) => {
                remarkValue = e.target.value || "";

                const amt = Number(refundAmount);
                const remarkOk = remarkValue.trim().length > 0;
                const amountOk =
                  refundAmount !== "" &&
                  Number.isFinite(amt) &&
                  amt > 0 &&
                  amt <= maxAmount;

                setConfirmCfg((c) => ({
                  ...c,
                  confirmDisabled: !(remarkOk && amountOk),
                }));
              }}
            />
            <div className="mt-1 text-xs text-gray-500">
              Remark is required to proceed.
            </div>
          </div>
        </div>
      ),
      onConfirm: async () => {
        const amountNum = Number(refundAmount);
        const remark = (remarkValue || "").trim();

        if (!remark) {
          toast.error("Please enter a remark.");
          return;
        }

        if (!refundAmount || !Number.isFinite(amountNum) || amountNum <= 0) {
          toast.error("Please enter a valid refund amount.");
          return;
        }

        if (amountNum > maxAmount) {
          toast.error(`Refund amount cannot exceed ₹${maxAmount.toFixed(2)}.`);
          return;
        }

        const payload = {
          orderId: orderObj.orderid,
          amount: amountNum.toFixed(2),
          remark,
        };

        const result = await easebuzzRefund(payload);

        if (result.success) {
          toast.success(result.message || "Easebuzz refund initiated");

          runInBackground(() => fetchOrderDetails(orderObj.orderid));
          runInBackground(() => fetchOrders());
          runInBackground(() => fetchRefunds());
          runInBackground(() => fetchSearch(orderObj.orderid));
        } else {
          toast.error(result.message || "Easebuzz refund failed");
        }
      },
    });
  };

  // const refundPayment = (orderObj) => {
  //   const amount = Math.trunc(Number(orderObj?.pay_amount ?? 0)).toString();
  //   const transactionId = String(orderObj?.virtual_acc || "");
  //   if (!transactionId) return toast.error("Missing transaction id (virtual account).");

  //   confirmAction({
  //     title: "Confirm Refund",
  //     tone: "danger",
  //     body: (
  //       <div className="text-sm">
  //         <div><b>Transaction ID:</b> {transactionId}</div>
  //         <div><b>Amount:</b> ₹{amount}</div>
  //       </div>
  //     ),
  //     onConfirm: async () => {
  //       try {
  //         const payload = { userid: orderObj?.fk_user_id || orderObj?.us_code || clientFilter || "", amount, transactionId };
  //         const res = await api.post("/Transactions/v1.0/refundProccess", payload);
  //         if (res?.data?.responseCode === "00") {
  //           toast.success(res?.data?.responseMessage || "Refund initiated");
  //           if (orderObj?.orderid) await fetchOrderDetails(orderObj.orderid);
  //         } else {
  //           toast.error(res?.data?.responseMessage || "Refund failed");
  //         }
  //       } catch { toast.error("Error processing refund"); }
  //     }
  //   });
  // };

  const generatePaymentIntentAction = (orderObj) => {
    const payload = {
      userid: orderObj?.fk_user_id || orderObj?.us_code,
      orderId: orderObj?.orderid,
      custName: orderObj?.customer_name || "N/A",
      custMobile: orderObj?.customer_mobile || "",
    };

    confirmAction({
      title: "Send Payment Link?",
      body: (
        <div className="text-sm">
          <div><b>User ID:</b> {payload.userid}</div>
          <div><b>Order ID:</b> {payload.orderId}</div>
          <div><b>Customer:</b> {payload.custName}</div>
          <div><b>Mobile:</b> {payload.custMobile}</div>
        </div>
      ),
      onConfirm: async () => {
        const result = await generatePaymentIntent(payload);

        if (result.success) {
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }
      },
    });
  };

  /* ---------- Effects ---------- */
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        setLatitude(pos.coords.latitude);
        setLongitude(pos.coords.longitude);
      });
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchRefunds();
  }, [adminId, clientFilter]); // eslint-disable-line

  useEffect(() => {
    if (tab === "refunds" && refundTab === "easebuzz") {
      fetchEasebuzzRefunds(easebuzzRefundPage);
    }
  }, [tab, refundTab, easebuzzRefundPage]); // eslint-disable-line

  /* ---------- UI ---------- */
  return (
    <div className="p-3 lg:p-5">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
            <Building2 size={18} />
          </div>
          <div>
            <h1 className="text-xl font-semibold">Order Controller</h1>
            <p className="text-sm text-gray-500">
              All actions are now inside the order details modal.
            </p>
          </div>
        </div>
        <button
          onClick={fetchOrders}
          disabled={loading}
          className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50"
        >
          <RefreshCcw size={16} /> {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Tabs */}
      <div className="mb-2 grid grid-cols-4 overflow-hidden rounded-2xl border bg-white">
        {[
          { key: "search", label: "Search by Order ID" },
          { key: "refunds", label: "Refund List" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => {
              setTab(t.key);
              if (t.key === "refunds") {
                /* lazy load */
              }
            }}
            className={cls(
              "border-b-4 px-3 py-2 text-sm font-semibold",
              tab === t.key
                ? "border-[#01C37D] text-[#01C37D]"
                : "border-transparent text-gray-500",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* TAB: SEARCH (kept as before, with colored buttons + confirms) */}
      {tab === "search" && (
        <div className="rounded-2xl border bg-white p-3">
          <div className="flex flex-wrap items-center gap-2">
            <input
              className="h-10 w-80 rounded-xl border px-3 text-sm outline-none"
              placeholder="Enter Order ID"
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchSearch(searchId)}
            />
            <button
              className="h-10 rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white"
              disabled={!searchId || searchLoading}
              onClick={() => fetchSearch(searchId)}
            >
              {searchLoading ? "Fetching…" : "Fetch Order"}
            </button>
          </div>

          {searchLoading ? (
            <div className="py-10 text-center text-sm text-gray-500">
              Loading…
            </div>
          ) : Object.keys(searchObj).length > 0 ? (

            <div className="mt-3 space-y-3">
              {/* header with badges */}
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border p-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-semibold truncate">
                      Order #{searchObj.orderid}
                    </h3>
                    <StatusBadge kind="order" value={searchObj.status} />
                    <StatusBadge kind="order" value={searchObj.orderType} />
                    <StatusBadge
                      kind="payment"
                      value={searchObj.payment_status}
                      extra={searchObj.payment_mode}
                    />
                    {searchObj.delivery_partner_status && (
                      <StatusBadge
                        kind="delivery"
                        value={searchObj.delivery_partner_status}
                      />
                    )}
                    {searchObj.refund_status === "Y" && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-rose-300 bg-rose-600/10 px-2.5 py-0.5 text-xs font-semibold text-rose-700">
                        <RotateCcw size={13} /> Refunded
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    Order: {searchObj.order_date || "N/A"} · Updated:{" "}
                    {searchObj.update || "N/A"}
                  </div>
                </div>


              </div>

              {/* (rest summary/items UI kept same as before)… */}
              <OrderSummaryAndItems data={searchObj} />
            </div>
          ) :
            (
              <div className="py-10 text-center text-sm text-gray-500">
                No data yet. Search an Order ID.
              </div>
            )}
        </div>
      )}

      {/* TAB: REFUNDS */}
      <div className="h-[75vh] overflow-scroll">
        {tab === "refunds" && (
          <RefundsTable
            refundTab={refundTab}
            setRefundTab={setRefundTab}
            refunds={refunds}
            loading={refundLoading}
            easebuzzRefunds={easebuzzRefunds}
            easebuzzRefundLoading={easebuzzRefundLoading}
            easebuzzRefundPage={easebuzzRefundPage}
            setEasebuzzRefundPage={setEasebuzzRefundPage}
            easebuzzRefundLimit={easebuzzRefundLimit}
            easebuzzRefundTotalOnPage={easebuzzRefundTotalOnPage}
            onRefreshCurrent={fetchRefunds}
            onRefreshEasebuzz={() => fetchEasebuzzRefunds(easebuzzRefundPage)}
            onCheckEasebuzzStatus={checkEasebuzzRefundStatus}
          />
        )}
      </div>


      {/* DETAILS MODAL */}
      <Transition show={detailsOpen} as={Fragment}>
        <Dialog onClose={() => setDetailsOpen(false)} className="relative z-50">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>
          <div className="fixed inset-0 grid place-items-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-150"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl rounded-2xl bg-white p-5 shadow-xl">
                <div className="mb-3 flex items-center justify-between">
                  <Dialog.Title className="text-base font-semibold">
                    Order Details
                  </Dialog.Title>
                  <button
                    className="h-8 rounded-xl border px-3 text-sm"
                    onClick={() => setDetailsOpen(false)}
                  >
                    Close
                  </button>
                </div>

                {/* Actions bar (all actions moved here) */}
                <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border bg-gray-50 p-2">
                  <button
                    className="inline-flex items-center gap-1 rounded-xl bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-700"
                    onClick={() => updatePaymentStatus(details?.orderid)}
                  >
                    <CreditCard size={14} /> Update Payment
                  </button>
                  {/* <button
                    className="inline-flex items-center gap-1 rounded-xl bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700"
                    onClick={() => generatePaymentIntentAction(details)}
                  >
                    <Link2 size={14} /> Payment Link
                  </button> */}
                  {String(details?.orderType) === "home_delivery" && (
                    <button
                      className="inline-flex items-center gap-1 rounded-xl bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
                      onClick={() =>
                        openSendToDeliveryPartner(
                          details?.orderid,
                          details?.fk_user_id ||
                          detailsRow?.us_code ||
                          clientFilter ||
                          "",
                        )
                      }
                    >
                      <Truck size={14} /> Send to DP
                    </button>
                  )}
                  <button
                    className="inline-flex items-center gap-1 rounded-xl bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"
                    onClick={() => refundPayment(details)}
                    disabled={
                      String(details?.payment_status || "").toLowerCase() ===
                      "unpaid" ||
                      !details?.virtual_acc ||
                      details?.refund_status === "Y"
                    }
                  >
                    <RotateCcw size={14} /> Refund Pay
                  </button>
                  <button
                    className="inline-flex items-center gap-1 rounded-xl bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"
                    onClick={() => refundPayment(searchObj)}
                    disabled={
                      String(searchObj.payment_status || "").toLowerCase() ===
                      "unpaid" ||
                      !searchObj.virtual_acc ||
                      searchObj.refund_status === "Y"
                    }
                  >
                    <RotateCcw size={14} /> Refund Pay
                  </button>
                  <button
                    className="inline-flex items-center gap-1 rounded-xl bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-700"
                    onClick={() => requestAddress(details?.orderid)}
                  >
                    <HelpCircle size={14} /> Request Address
                  </button>

                  <button
                    className="inline-flex items-center gap-1 rounded-xl bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
                    onClick={() => checkPaytmStatus(details?.orderid)}
                  >
                    <RefreshCcw size={14} /> Paytm Status
                  </button>
                </div>

                <div className="max-h-[68vh] overflow-auto space-y-3">
                  {detailsBusy ? (
                    <div className="py-6 text-center text-gray-500">
                      Loading details…
                    </div>
                  ) : Object.keys(details || {}).length >= 0 ? (
                    <>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm">
                        <div>
                          <div>
                            <h3>{details.us_name || "N/A"}</h3>
                          </div>
                          <div>
                            <b>Order #:</b> {details.orderid || "N/A"}
                          </div>
                          <div>
                            <b>Type:</b> {details.orderType || "N/A"}
                          </div>
                          <div className="flex items-center gap-2">
                            <b>Status:</b>{" "}
                            <StatusBadge kind="order" value={details.status} />
                          </div>
                          <div className="flex items-center gap-2">
                            <b>Payment:</b>{" "}
                            <StatusBadge
                              kind="payment"
                              value={details.payment_status}
                              extra={details.payment_mode}
                            />
                          </div>
                        </div>
                        <div>
                          <div>
                            <b>Order Date:</b> {details?.order_date || "N/A"}
                          </div>
                          <div>
                            <b>Updated:</b> {details.update || "N/A"}
                          </div>
                          <div>
                            <b>Total:</b> ₹{fmt(details.pay_amount)}
                          </div>
                          <div>
                            <b>Subtotal:</b> ₹{fmt(details.subtotal)}
                          </div>
                        </div>
                      </div>

                      {/* address */}
                      {(() => {
                        const a = safeParse(details?.address_json).values || {};
                        return (
                          <div className="rounded-2xl border p-3 text-sm">
                            <div>
                              <b>Customer:</b> {details.customer_name || "N/A"}{" "}
                              <span className="text-gray-500">
                                ({details.customer_mobile || "N/A"})
                              </span>
                            </div>
                            <div className="mt-1 text-xs text-gray-600">
                              <div>{details.address_line || ""}</div>
                              <div>
                                {a?.address || ""} {a?.landmark_area || ""}{" "}
                                {a?.building_name || ""} {a?.floor_number || ""}{" "}
                                {a?.tower_number || ""} {a?.city || ""}{" "}
                                {a?.state || ""} {a?.in_pin_code || ""}
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* items */}
                      <div className="overflow-auto rounded-2xl border">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr className="text-left">
                              {[
                                "#",
                                "Product",
                                "Qty",
                                "Item Price",
                                "Total",
                              ].map((h) => (
                                <th key={h} className="px-3 py-2">
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(details.items || []).map((it, i) => (
                              <tr
                                key={`${it.id || it.product_id}-${i}`}
                                className="border-t"
                              >
                                <td className="px-3 py-2">{i + 1}</td>
                                <td className="px-3 py-2">
                                  {it.product_id || it.item_name || "—"}
                                </td>
                                <td className="px-3 py-2">
                                  {it.quantity ?? "—"}
                                </td>
                                <td className="px-3 py-2">
                                  ₹{fmt(it.item_price)}
                                </td>
                                <td className="px-3 py-2">
                                  ₹{fmt(it.total_price)}
                                </td>
                              </tr>
                            ))}
                            {(details.items || []).length === 0 && (
                              <tr>
                                <td
                                  className="px-3 py-4 text-center text-gray-500"
                                  colSpan={5}
                                >
                                  No items
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) :
                    (
                      <div className="py-6 text-center text-gray-500">
                        No details available.
                      </div>
                    )}
                </div>

                {/* footer: status changer with confirm */}
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <div className="text-xs text-gray-600">Change Status</div>
                  <select
                    className="h-9 rounded-xl border bg-white px-2 text-xs outline-none"
                    value={rowStatus}
                    onChange={(e) => setRowStatus(e.target.value)}
                  >
                    <option value="">Select status</option>
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  <button
                    className="h-9 rounded-xl bg-emerald-600 px-3 text-xs font-medium text-white hover:bg-emerald-700"
                    onClick={() =>
                      confirmChangeOrderStatus(
                        rowStatus,
                        detailsRow?.orderid,
                        details?.fk_user_id ||
                        detailsRow?.us_code ||
                        clientFilter ||
                        "",
                      )
                    }
                    disabled={detailsBusy || !rowStatus}
                  >
                    {detailsBusy ? "Updating…" : "Update Status"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* shared dialogs */}
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={confirmCfg.title}
        body={confirmCfg.body}
        tone={confirmCfg.tone}
        busy={confirmCfg.busy}
        confirmDisabled={confirmCfg.confirmDisabled} // ✅ NEW
        onConfirm={confirmCfg.onConfirm}
      />
      <SelectDialog
        open={selectOpen}
        onClose={() => setSelectOpen(false)}
        title={selectCfg.title}
        options={selectCfg.options}
        selected={selectCfg.selected}
        setSelected={selectCfg.setSelected}
        confirmText="Send"
        busy={selectCfg.busy}
        onConfirm={selectCfg.onConfirm}
      />

      <PaytmStatusDialog
        open={paytmOpen}
        onClose={() => setPaytmOpen(false)}
        loading={paytmLoading}
        data={paytmData}
      />
      <EasebuzzRefundStatusDialog
        open={easebuzzStatusOpen}
        onClose={() => setEasebuzzStatusOpen(false)}
        loading={easebuzzStatusLoading}
        data={easebuzzStatusData}
      />
    </div>
  );
}

/* ---------- Small sub-views kept for Search tab ---------- */
function OrderSummaryAndItems({ data }) {
  const pairs = (data.items || []).map((it) => parseCustomization(it.customization));
  return (
    <>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-2xl border p-3">
          <div className="text-sm font-semibold mb-2">Summary</div>
          <div className="text-sm"><b>Total:</b> ₹{fmt(data.pay_amount)}</div>
          <div className="text-sm"><b>Subtotal:</b> ₹{fmt(data.subtotal)}</div>
          <div className="text-sm"><b>Platform:</b> {data.platform || "N/A"}</div>
          <div className="text-sm"><b>Virtual Acc:</b> {data.virtual_acc || "N/A"}</div>
          <div className="text-sm"><b>GST Claim:</b> {data.gst_claim || "N/A"}</div>
          <div className="text-sm"><b>Loyalty Points:</b> {data.loyalty_points || "0"}</div>
        </div>
        <div className="rounded-2xl border p-3">
          <div className="text-sm font-semibold mb-2">Customer & Address</div>
          <div className="text-sm"><b>Name:</b> {data.customer_name || "N/A"}</div>
          <div className="text-sm"><b>Phone:</b> {data.customer_mobile || "N/A"}</div>
          <div className="text-xs text-gray-600">{data.address_line}</div>
          {(() => {
            const a = safeParse(data?.address_json).values || {};
            return (
              <div className="text-xs text-gray-600">
                {a?.address || ""} {a?.landmark_area || ""} {a?.building_name || ""} {a?.floor_number || ""}{" "}
                {a?.tower_number || ""} {a?.city || ""} {a?.state || ""} {a?.in_pin_code || ""}
              </div>
            );
          })()}
        </div>
      </div>

      <div className="overflow-auto rounded-2xl border">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left">
              {["#", "Product", "Qty", "Item Price", "Total", "Customization"].map(h => (
                <th key={h} className="px-3 py-2">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.items || []).map((it, i) => {
              const ps = pairs[i] || [];
              return (
                <tr key={`${it.id || it.product_id}-${i}`} className="border-t align-top">
                  <td className="px-3 py-2">{i + 1}</td>
                  <td className="px-3 py-2">{it.product_id || it.item_name || "—"}</td>
                  <td className="px-3 py-2">{it.quantity ?? "—"}</td>
                  <td className="px-3 py-2">₹{fmt(it.item_price)}</td>
                  <td className="px-3 py-2">₹{fmt(it.total_price)}</td>
                  <td className="px-3 py-2">
                    {ps.length === 0 ? (
                      <span className="text-gray-400">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {ps.map(([k, v]) => (
                          <span key={`${k}-${v}-${i}`} className="rounded-full border bg-gray-50 px-2 py-0.5 text-xs">
                            <b>{k}:</b> {String(v)}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {(data.items || []).length === 0 && (
              <tr><td className="px-3 py-4 text-center text-gray-500" colSpan={6}>No items</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function RefundsTable({
  refundTab,
  setRefundTab,
  refunds,
  loading,
  easebuzzRefunds,
  easebuzzRefundLoading,
  easebuzzRefundPage,
  setEasebuzzRefundPage,
  easebuzzRefundLimit,
  easebuzzRefundTotalOnPage,
  onRefreshCurrent,
  onRefreshEasebuzz,
  onCheckEasebuzzStatus,
}) {
  const isCurrent = refundTab === "current";
  const isEasebuzz = refundTab === "easebuzz";

  return (
    <div className="rounded-2xl border bg-white">
      <div className="border-b p-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex rounded-xl border bg-gray-50 p-1">
            <button
              onClick={() => setRefundTab("current")}
              className={cls(
                "rounded-lg px-4 py-2 text-sm font-semibold",
                isCurrent ? "bg-[#01C37D] text-white" : "text-gray-600"
              )}
            >
              Current Refunds
            </button>
            <button
              onClick={() => setRefundTab("easebuzz")}
              className={cls(
                "rounded-lg px-4 py-2 text-sm font-semibold",
                isEasebuzz ? "bg-[#01C37D] text-white" : "text-gray-600"
              )}
            >
              Easebuzz Refunds
            </button>
          </div>

          <button
            onClick={isCurrent ? onRefreshCurrent : onRefreshEasebuzz}
            className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50"
          >
            <RefreshCcw size={15} />
            Refresh
          </button>
        </div>
      </div>

      {isCurrent && (
        <>
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-500">Loading refunds…</div>
          ) : refunds.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500">No refund records found.</div>
          ) : (
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-[#01C37D] text-white">
                  <tr className="text-left">
                    {["ID", "User ID", "VA Account", "Sender Acc", "Created On", "Updated", "Status", "Amount", "Bank RRN", "Mode", "Order ID", "Customer Mobile", "Remark"].map((h) => (
                      <th key={h} className="px-3 py-2 font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {refunds.map((r) => (
                    <tr key={r.id} className="border-t">
                      <td className="px-3 py-2">{r.id}</td>
                      <td className="px-3 py-2">{r.userid}</td>
                      <td className="px-3 py-2">{r.va_account}</td>
                      <td className="px-3 py-2">{r.sender_acc || "N/A"}</td>
                      <td className="px-3 py-2">{r.created_on}</td>
                      <td className="px-3 py-2">{r.update}</td>
                      <td className="px-3 py-2"><StatusBadge kind="payment" value={r.status} /></td>
                      <td className="px-3 py-2">₹{fmt(r.amount)}</td>
                      <td className="px-3 py-2">{r.bank_rrn || "N/A"}</td>
                      <td className="px-3 py-2">{r.mode}</td>
                      <td className="px-3 py-2">{r.order_id || "N/A"}</td>
                      <td className="px-3 py-2">{r.customer_mobile || "N/A"}</td>
                      <td className="px-3 py-2">{r.refund_remark || "N/A"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {isEasebuzz && (
        <>
          {easebuzzRefundLoading ? (
            <div className="py-10 text-center text-sm text-gray-500">Loading Easebuzz refunds…</div>
          ) : easebuzzRefunds.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500">No Easebuzz refund records found.</div>
          ) : (
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-[#01C37D] text-white">
                  <tr className="text-left">
                    {[
                      "PK ID",
                      "Order ID",
                      "Paytm Order ID",
                      "Refund Amount",
                      "Remark",
                      "Refund ID",
                      "Request ID",
                      "User ID",
                      "Created At",
                      "Status",
                      "Easebuzz ID",
                      "Action",
                    ].map((h) => (
                      <th key={h} className="px-3 py-2 font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {easebuzzRefunds.map((r) => (
                    <tr key={r.pk_id} className="border-t">
                      <td className="px-3 py-2">{r.pk_id}</td>
                      <td className="px-3 py-2">{r.order_id || "N/A"}</td>
                      <td className="px-3 py-2">{r.paytm_order_id || "N/A"}</td>
                      <td className="px-3 py-2">₹{fmt(r.refund_amount)}</td>
                      <td className="px-3 py-2">{r.refund_remark || "N/A"}</td>
                      <td className="px-3 py-2">{r.refund_id || "N/A"}</td>
                      <td className="px-3 py-2">{r.refund_request_id || "N/A"}</td>
                      <td className="px-3 py-2">{r.fk_user_id || "N/A"}</td>
                      <td className="px-3 py-2">{r.created_at || "N/A"}</td>
                      <td className="px-3 py-2">
                        <StatusBadge kind="payment" value={r.refund_status || "N/A"} />
                      </td>
                      <td className="px-3 py-2">{r.easebuzzid || "N/A"}</td>
                      <td className="px-3 py-2">
                        <button
                          className="inline-flex items-center gap-1 rounded-xl bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                          onClick={() => onCheckEasebuzzStatus(r)}
                          disabled={!r.easebuzzid || r.refund_status === "refunded"}
                        >
                          <RefreshCcw size={13} /> Status
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between border-t px-3 py-3 text-sm">
            <span>
              Page <b>{easebuzzRefundPage}</b> · Limit <b>{easebuzzRefundLimit}</b>
            </span>

            <div className="flex gap-2">
              <button
                className="h-9 rounded-xl border px-3 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={easebuzzRefundPage <= 1}
                onClick={() =>
                  setEasebuzzRefundPage((p) => Math.max(1, p - 1))
                }
              >
                Previous
              </button>

              <button
                className="h-9 rounded-xl border px-3 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={easebuzzRefundTotalOnPage < easebuzzRefundLimit}
                onClick={() => setEasebuzzRefundPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
